import '$app/i18n';
import '@vueron/kiska/style.css';
import '$app/style.css';

import { extend } from '@react-three/fiber';
import * as ReactDOM from 'react-dom/client';

import { initializeEnv } from '@vueron/kiska';

import { CustomPointsMaterial } from '$viewer/component/three/CustomPointMaterial';
import { CustomPointsMaterial2 } from '$viewer/component/three/CustomPointMaterial2';

// runtime-env.js(entrypoint.sh/server.mjs)가 주입한 window['VITE_*'] 런타임 값을 설정
const runtimeEnv = Object.fromEntries(
  Object.keys(window)
    .filter(key => key.startsWith('VITE_'))
    .map(key => [key, (window as Record<string, unknown>)[key]]),
);

// 빌드타임 import.meta.env 위에 런타임 값을 덮어써 docker env 변경이 반영되도록 함
initializeEnv({ ...import.meta.env, ...runtimeEnv });

extend({ CustomPointsMaterial });
extend({ CustomPointsMaterial2 });

// VPF-0: React 19 dev 빌드는 커밋마다 컴포넌트 성능 트랙을 performance.measure(detail.devtools)로
// 기록하는데, 이 엔트리는 User Timing 버퍼에 무한 누적된다(뷰어 스트리밍 기준 초당 ~3,000개).
// 장시간 파일 재생 시 이 누적/방출 오버헤드만으로 렌더러가 OOM(Aw Snap, error code 5)에 도달하는 것을
// 확인했고, 아래 차단만으로 크래시가 해소되는 것을 격리 실험으로 검증했다(13분+ 재생 생존).
// prod 빌드에는 이 계측이 없어 영향 없음. 부작용: DevTools Performance 패널의 React 트랙이
// dev 에서 비활성화됨. 조사 상세: docs/vpf-0-viewer-dev-memory-leak-continuation.md
if (import.meta.env.DEV) {
  const originalMeasure = performance.measure.bind(performance);
  performance.measure = ((
    measureName: string,
    startOrMeasureOptions?: string | PerformanceMeasureOptions,
    endMark?: string,
  ) => {
    if (
      startOrMeasureOptions &&
      typeof startOrMeasureOptions === 'object' &&
      (startOrMeasureOptions.detail as Nullish<{ devtools?: unknown }>)
        ?.devtools
    ) {
      return undefined as unknown as PerformanceMeasure;
    }
    return originalMeasure(measureName, startOrMeasureOptions, endMark);
  }) as typeof performance.measure;
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const main = () => {
  import('$app/App').then(({ default: App }) => {
    root.render(<App />);
  });
};

// VPF-4453 임시 메모리 계측 (localhost 전용 동작) — 절대 커밋하지 말 것.
// 전역 패치(WebSocket/Worker/리스너/타이머 카운트)가 App 모듈 로드보다 먼저
// 설치되도록 프로브 로드 후에 main() 을 호출한다.
import('@/util/leakProbe')
  .then(({ startLeakProbe }) => {
    startLeakProbe();
  })
  .finally(main);
